import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Users, Layers, Sparkles, Target, Compass, Settings2, Map } from "lucide-react";
import SystemCard from "@/components/SystemCard";
import SectionHeading from "@/components/SectionHeading";
import SEO, { SITE_URL } from "@/components/SEO";
import SystemCombinations from "@/components/SystemCombinations";
import GeoDefinition, { buildDefinedTerm, buildFaqJsonLd } from "@/components/GeoDefinition";
import { SYSTEMS, HERO_IMAGE } from "@/lib/constants";

const PINE_DEFINITION =
  "PINE Systems ist ein Anbieter für moderne XR-Erlebnisstandorte und digitale Markenräume. PINE Systems übersetzt Fläche, Zielgruppe, Betreiberlogik und Customer Journey in fünf klar getrennte Systemprofile – PVRA, AR_ENA, PINE VR, Hotel VR und PINE Places – und schafft daraus betreibbare Standortlösungen für Freizeitflächen, Hotels, Eventlocations, touristische Betriebe, Showrooms und Markenräume.";

const PINE_QA = [
  {
    q: "Was bietet PINE Systems?",
    a: "Fünf Systemprofile für unterschiedliche Standortlogiken: zwei Arena-Systeme (PVRA, AR_ENA), zwei kompakte VR-Erlebnisformate (PINE VR, Hotel VR) und ein digitales Markenraum-System (PINE Places).",
  },
  {
    q: "Für wen ist PINE Systems gedacht?",
    a: "Für Betreiber von Freizeitflächen, Hotels, Resorts, Eventlocations, touristischen Betrieben, Showrooms, Immobilien und Markenräumen, die aus einer Fläche ein betreibbares Erlebnisangebot machen möchten.",
  },
  {
    q: "Wie startet ein PINE-Systems-Projekt?",
    a: "Mit einer strukturierten Standortprüfung: Fläche, Raumlogik, Zielgruppe, Customer Journey und Betreiberstruktur werden bewertet. Daraus leitet PINE Systems das passende Systemprofil ab.",
  },
];

const HOME_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "PINE Systems",
    legalName: "PINE Gaming GmbH",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    sameAs: [],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Lehfeld 1",
      postalCode: "21029",
      addressLocality: "Hamburg",
      addressCountry: "DE",
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: "partner@pinexs.de",
      telephone: "+49-151-22202892",
      contactType: "sales",
      areaServed: "DE",
      availableLanguage: ["German"],
    },
    description:
      "PINE Systems entwickelt Systemlösungen für moderne Erlebnisstandorte und digitale Markenräume.",
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "PINE Systems",
    url: SITE_URL,
    inLanguage: "de-DE",
  },
  buildDefinedTerm({
    name: "PINE Systems",
    alternateName: ["PINE", "PINE Systems Germany"],
    description: PINE_DEFINITION,
    url: SITE_URL,
  }),
  buildFaqJsonLd(PINE_QA),
];

const PROBLEM_CARDS = [
  {
    icon: MapPin,
    title: "Standort",
    text: "Fläche, Raumhöhe, Grundriss, Infrastruktur und Gästewege.",
  },
  {
    icon: Layers,
    title: "Betrieb",
    text: "Personal, Einweisung, Ablauf, Sicherheit, Qualität und Wiederholbarkeit.",
  },
  {
    icon: Users,
    title: "Zielgruppe",
    text: "Familien, Gruppen, Firmen, Gäste, Tourismus, Events oder Competition.",
  },
  {
    icon: Sparkles,
    title: "Erlebnisprofil",
    text: "Das passende System für den jeweiligen Standorttyp.",
  },
];

const HEADSET_CARDS = [
  {
    title: "Technik allein verkauft kein Erlebnis.",
    text:
      "Ein Headset erklärt noch nicht, warum Gäste buchen sollen. Entscheidend ist, welches Erlebnis entsteht und für wen es relevant ist.",
  },
  {
    title: "Fläche allein ist kein Geschäftsmodell.",
    text:
      "Ob ein Standort funktioniert, hängt von Raumlogik, Gästewegen, Personal, Sicherheit, Zielgruppe und Angebotsstruktur ab.",
  },
  {
    title: "Content allein schafft keinen Betrieb.",
    text:
      "Erlebnisinhalte müssen in Einweisung, Buchung, Ablauf, Betreuung und Wiederholung übersetzt werden.",
  },
];

const VALUE_CARDS = [
  {
    icon: Target,
    title: "Standortpotenzial erkennen",
    text:
      "Wir prüfen, welches Systemprofil grundsätzlich zu Fläche, Zielgruppe, Betreiberstruktur und Nutzungsidee passt.",
  },
  {
    icon: Sparkles,
    title: "Erlebnisangebot entwickeln",
    text:
      "Aus technischen Möglichkeiten entstehen buchbare Angebote für Gruppen, Gäste, Firmen, Familien, Tourismus oder Events.",
  },
  {
    icon: Settings2,
    title: "Betrieb vereinfachen",
    text:
      "Einweisung, Ablauf, Betreuung, Schulung und Support werden von Anfang an mitgedacht.",
  },
  {
    icon: Users,
    title: "Zielgruppen aktivieren",
    text:
      "Jedes Systemprofil spricht andere Buchungsanlässe an: Freizeit, Hospitality, Competition, Teambuilding, Schlechtwetter oder digitale Customer Journey.",
  },
  {
    icon: Map,
    title: "Customer Journey erweitern",
    text:
      "Mit PINE Places werden reale Orte digital erlebbar und schon vor dem Besuch Teil der Markenwahrnehmung.",
  },
  {
    icon: Compass,
    title: "Entscheidung strukturieren",
    text:
      "PINE Systems führt Interessenten nicht direkt in den Kauf, sondern in eine saubere Prüfung von Standort, Produktfit und nächstem Schritt.",
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title="PINE Systems – Erlebnisstandorte und digitale Markenräume mit System"
        description="PINE Systems entwickelt professionelle XR-Erlebnisstandorte und digitale Markenräume für Betreiber, Gründer, Freizeitflächen, Eventlocations, Hotels und touristische Betriebe."
        jsonLd={HOME_JSONLD}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[rgba(255,255,255,0.08)]">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#050607] via-[#050607]/85 to-transparent" />
          <div className="absolute inset-0 grid-overlay opacity-50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-24 md:pt-40 md:pb-36">
          <div className="overline reveal">PINE SYSTEMS · ERLEBNISSTANDORTE & MARKENRÄUME</div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl uppercase mt-6 max-w-5xl leading-[1.02] reveal reveal-delay-1">
            Aus Flächen werden
            <br /> <span className="text-[#18E096]">Erlebnisse.</span>
            <br /> Aus Erlebnissen wird System.
          </h1>
          <p className="text-[#AEB7B7] text-base md:text-xl mt-8 max-w-3xl leading-relaxed reveal reveal-delay-2">
            PINE Systems entwickelt professionelle XR-Erlebnisstandorte und digitale Markenräume
            für Betreiber, Gründer, Freizeitflächen, Eventlocations, Hotels und touristische Betriebe.
          </p>
          <p className="text-[#AEB7B7] text-sm md:text-base mt-4 max-w-3xl leading-relaxed reveal reveal-delay-3">
            Wir verbinden Erlebnis, Technologie, Content, Betrieb, Einweisung, Schulung, Support
            und Standortlogik zu einem betreibbaren Gesamtsystem.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 reveal reveal-delay-4">
            <Link to="/standort-pruefen" className="btn-primary" data-testid="hero-cta-primary">
              Standortpotenzial prüfen <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/systeme" className="btn-secondary" data-testid="hero-cta-secondary">
              Systeme ansehen
            </Link>
          </div>
        </div>

        {/* Trust strip */}
        <div className="relative border-t border-[rgba(255,255,255,0.08)] bg-[#050607]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-[#AEB7B7] text-sm uppercase tracking-[0.18em]">
              <span className="text-white">Mehr</span> als Hardware.
            </div>
            <div className="text-[#AEB7B7] text-sm uppercase tracking-[0.18em]">
              <span className="text-white">Mehr</span> als ein Raum.
            </div>
            <div className="text-[#AEB7B7] text-sm uppercase tracking-[0.18em]">
              <span className="text-white">Ein System</span> für moderne Erlebnisstandorte.
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="01 · Ausgangslage"
            title={<>Fläche allein <br /> reicht nicht.</>}
            subtitle="Viele Standorte haben Fläche. Viele suchen neue Optionen. Viele Technologien wirken spannend. Aber aus Fläche, Technik und Idee entsteht noch kein funktionierendes Erlebnisgeschäft. Dafür braucht es Systemlogik."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-14 border-l border-t border-[rgba(255,255,255,0.08)]">
            {PROBLEM_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 hover:bg-[#0B0F10] transition-colors group"
                  data-testid={`problem-card-${i}`}
                >
                  <Icon
                    size={28}
                    strokeWidth={1.25}
                    className="text-[#18E096] group-hover:scale-110 transition-transform"
                  />
                  <div className="overline mt-6 text-[#AEB7B7]">0{i + 1}</div>
                  <h3 className="font-display text-xl uppercase mt-2 text-white">{c.title}</h3>
                  <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HEADSET / SCHEITERN */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="02 · Was wirklich entscheidet"
            title={<>Warum viele XR-Projekte <br className="hidden md:block" /> nicht am Headset scheitern.</>}
            subtitle="Technologie ist selten das eigentliche Problem. Entscheidend ist, ob aus Technik ein Angebot entsteht, das Gäste verstehen, buchen, weitererzählen und wiederholen können."
          />
          <p className="text-[#AEB7B7] mt-8 max-w-3xl text-base leading-relaxed">
            Viele Standorte starten mit der Frage nach Hardware. PINE Systems startet früher:
            bei Fläche, Zielgruppe, Erlebnislogik, Betrieb und Customer Journey. Denn ein
            erfolgreicher Erlebnisstandort braucht mehr als Geräte. Er braucht ein klares
            Angebot, geschulte Abläufe, verständliche Buchungsanlässe und ein System, das im
            Alltag funktioniert.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {HEADSET_CARDS.map((c, i) => (
              <div
                key={i}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 hover:bg-[#0B0F10] transition-colors"
                data-testid={`headset-card-${i}`}
              >
                <div className="overline text-[#AEB7B7]">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-xl uppercase mt-3 text-white leading-tight">
                  {c.title}
                </h3>
                <p className="text-[#AEB7B7] text-sm mt-4 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/standort-pruefen" className="btn-secondary" data-testid="headset-cta">
              Standortpotenzial prüfen <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* VALUE / WAS PINE LEISTET */}
      <section className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="03 · Leistung"
            title="Was PINE Systems für Betreiber leistet."
            subtitle="PINE Systems übersetzt moderne XR- und Erlebnisformate in betreibbare Standortlogik."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {VALUE_CARDS.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={i}
                  className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 bg-[#0B0F10] hover:bg-[#141819] transition-colors group"
                  data-testid={`value-card-${i}`}
                >
                  <Icon
                    size={24}
                    strokeWidth={1.25}
                    className="text-[#18E096] group-hover:scale-110 transition-transform"
                  />
                  <div className="overline mt-5 text-[#AEB7B7]">0{i + 1}</div>
                  <h3 className="font-display text-lg md:text-xl uppercase mt-2 text-white leading-tight">
                    {v.title}
                  </h3>
                  <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10">
            <Link to="/standort-pruefen" className="btn-primary" data-testid="value-cta">
              Welches System passt zu Ihrem Standort? <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* PATH SWITCHER — Standort oder Gründer */}
      <section className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]" data-testid="path-switcher">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="overline mb-4">Zwei Wege</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] max-w-4xl">
            Wo stehen Sie <span className="text-[#18E096]">gerade?</span>
          </h2>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            PINE Systems kann sowohl bestehende Standorte prüfen als auch Gründer vor der
            Flächensuche strukturiert einordnen.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            <div
              className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 md:p-10 bg-[#0B0F10] hover:bg-[#141819] transition-colors flex flex-col"
              data-testid="path-card-standort"
            >
              <div className="overline text-[#18E096]">01 · Standort vorhanden</div>
              <h3 className="font-display text-2xl md:text-3xl uppercase mt-5 text-white leading-tight">
                Ich habe eine Fläche.
              </h3>
              <p className="text-[#AEB7B7] mt-4 leading-relaxed flex-1">
                Sie haben bereits einen Standort, ein Hotel, eine Eventlocation, Freizeitfläche
                oder Immobilie und möchten prüfen, welches PINE Systems Profil dazu passt.
              </p>
              <div className="mt-8">
                <Link
                  to="/standort-pruefen"
                  className="btn-primary"
                  data-testid="path-cta-standort"
                >
                  Standortpotenzial prüfen <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
            <div
              className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 md:p-10 bg-[#0B0F10] hover:bg-[#141819] transition-colors flex flex-col"
              data-testid="path-card-gruenden"
            >
              <div className="overline text-[#18E096]">02 · Gründerpfad</div>
              <h3 className="font-display text-2xl md:text-3xl uppercase mt-5 text-white leading-tight">
                Ich möchte gründen.
              </h3>
              <p className="text-[#AEB7B7] mt-4 leading-relaxed flex-1">
                Sie interessieren sich für eine eigene Arena oder einen Erlebnisstandort,
                haben aber noch keine passende Fläche.
              </p>
              <div className="mt-8">
                <Link to="/gruenden" className="btn-secondary" data-testid="path-cta-gruenden">
                  Gründer-Erstgespräch anfragen <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SYSTEMS */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="04 · Systemprofile"
            title={
              <>
                Fünf Systemprofile. <br />
                Ein Ziel: Erlebnisstandorte und Markenräume, die funktionieren.
              </>
            }
            subtitle="PINE Systems prüft Fläche, Betreiberlogik, Zielgruppe, Nutzungspotenzial und Customer Journey und leitet daraus das passende Systemprofil ab."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-14">
            {SYSTEMS.map((s, i) => (
              <SystemCard key={s.slug} s={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* MID CTA */}
      <section className="border-b border-[rgba(255,255,255,0.08)] relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">05 · Positionierung</div>
            <h2 className="font-display text-3xl md:text-5xl uppercase leading-tight">
              Wir machen aus <span className="text-[#18E096]">Räumen</span> Welten.
            </h2>
            <p className="text-[#AEB7B7] text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              Das Ziel ist kein Technikraum. Das Ziel ist ein Angebot, das Kunden verstehen,
              buchen und gemeinsam erleben können. Der passende Systemfit entsteht aus Standort,
              Zielgruppe und Journey.
            </p>
          </div>
          <div className="lg:col-span-5 lg:justify-self-end">
            <Link to="/standort-pruefen" className="btn-primary" data-testid="mid-cta">
              Standortpotenzial prüfen <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ABLAUF TEASER */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="06 · Ablauf"
            title="Vom ersten Interesse zum passenden Systemprofil."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-12 border-t border-l border-[rgba(255,255,255,0.08)]">
            {[
              ["01", "Anfrage", "Standortdaten senden"],
              ["02", "Erstgespräch", "Ausgangslage, Ziel, Nutzungsidee"],
              ["03", "Produktfit", "Systemprofil ableiten"],
            ].map(([n, t, d], i) => (
              <div
                key={i}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 bg-[#0B0F10]"
              >
                <div className="font-display text-4xl text-[#18E096]">{n}</div>
                <div className="font-display text-lg uppercase mt-4">{t}</div>
                <p className="text-[#AEB7B7] text-sm mt-2">{d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/ablauf" className="btn-secondary" data-testid="ablauf-link">
              Vollständigen Ablauf ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* SYSTEM COMBINATIONS */}
      <SystemCombinations />

      {/* GEO / AI SEARCH READINESS */}
      <GeoDefinition
        term="PINE Systems"
        overline="GEO · Definition"
        definition={PINE_DEFINITION}
        qa={PINE_QA}
        variant="muted"
      />

      {/* FINAL CTA */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <div className="overline mb-4 mx-auto">Nächster Schritt</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-tight max-w-4xl mx-auto">
            Welches System passt zu <br className="hidden md:block" />
            <span className="text-[#18E096]">Ihrem Standort?</span>
          </h2>
          <p className="text-[#AEB7B7] mt-6 max-w-2xl mx-auto">
            Der passende Produktfit entsteht aus Fläche, Raumlogik, Gästewegen, Personal,
            Zielgruppe, Infrastruktur, Betrieb und Customer Journey.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/standort-pruefen" className="btn-primary" data-testid="final-cta">
              Standortpotenzial prüfen <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/kontakt" className="btn-secondary">
              Erstgespräch anfragen
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
